import { z } from "zod";

import { TRANSACTION_STOCK_EFFECT_VALUES, valueMap } from "../platform/domain-values";
import { TRANSACTION_MESSAGES, TRANSACTION_TYPE_LABELS } from "./inventory-copy";

export const TRANSACTION_TYPE_VALUES = ["PURCHASE", "SALE", "USAGE", "DISPOSAL", "OPENING_BALANCE", "CORRECTION", "BATCH_SPLIT"] as const;
export const transactionTypeSchema = z.enum(TRANSACTION_TYPE_VALUES);
export type TransactionType = z.infer<typeof transactionTypeSchema>;

export const TRANSACTION_STOCK_EFFECTS = valueMap(TRANSACTION_STOCK_EFFECT_VALUES);
export type TransactionStockEffect = typeof TRANSACTION_STOCK_EFFECT_VALUES[number];
export type TransactionCounterpartyKind = "supplier" | "purchaser";
export type TransactionFieldMode = "none" | "optional" | "required";
export type TransactionRole = "admin" | "manager" | "chemist";

export type TransactionTypeMetadata = {
  label: string;
  stockEffect: TransactionStockEffect;
  inventoryAppliedDefault: boolean;
  inventoryAppliedEditable: boolean;
  allowedRoles: readonly TransactionRole[];
  supplierMode: TransactionFieldMode;
  purchaserMode: TransactionFieldMode;
  priceMode: TransactionFieldMode;
  counterpartyKind: TransactionCounterpartyKind | null;
  requiresFinalCountedAmount: boolean;
};

export const TRANSACTION_TYPE_METADATA = {
  PURCHASE: {
    label: TRANSACTION_TYPE_LABELS.PURCHASE,
    stockEffect: TRANSACTION_STOCK_EFFECTS.increase,
    inventoryAppliedDefault: true,
    inventoryAppliedEditable: false,
    allowedRoles: ["admin", "manager"],
    supplierMode: "required",
    purchaserMode: "none",
    priceMode: "optional",
    counterpartyKind: "supplier",
    requiresFinalCountedAmount: false,
  },
  SALE: {
    label: TRANSACTION_TYPE_LABELS.SALE,
    stockEffect: TRANSACTION_STOCK_EFFECTS.decrease,
    inventoryAppliedDefault: true,
    inventoryAppliedEditable: false,
    allowedRoles: ["admin", "manager"],
    supplierMode: "none",
    purchaserMode: "required",
    priceMode: "optional",
    counterpartyKind: "purchaser",
    requiresFinalCountedAmount: false,
  },
  USAGE: {
    label: TRANSACTION_TYPE_LABELS.USAGE,
    stockEffect: TRANSACTION_STOCK_EFFECTS.decrease,
    inventoryAppliedDefault: true,
    inventoryAppliedEditable: false,
    allowedRoles: ["admin", "manager", "chemist"],
    supplierMode: "none",
    purchaserMode: "none",
    priceMode: "none",
    counterpartyKind: null,
    requiresFinalCountedAmount: false,
  },
  DISPOSAL: {
    label: TRANSACTION_TYPE_LABELS.DISPOSAL,
    stockEffect: TRANSACTION_STOCK_EFFECTS.decrease,
    inventoryAppliedDefault: true,
    inventoryAppliedEditable: false,
    allowedRoles: ["admin", "manager", "chemist"],
    supplierMode: "none",
    purchaserMode: "none",
    priceMode: "none",
    counterpartyKind: null,
    requiresFinalCountedAmount: false,
  },
  OPENING_BALANCE: {
    label: TRANSACTION_TYPE_LABELS.OPENING_BALANCE,
    stockEffect: TRANSACTION_STOCK_EFFECTS.increase,
    inventoryAppliedDefault: true,
    inventoryAppliedEditable: false,
    allowedRoles: ["admin", "manager", "chemist"],
    supplierMode: "optional",
    purchaserMode: "none",
    priceMode: "optional",
    counterpartyKind: "supplier",
    requiresFinalCountedAmount: false,
  },
  CORRECTION: {
    label: TRANSACTION_TYPE_LABELS.CORRECTION,
    stockEffect: TRANSACTION_STOCK_EFFECTS.computed,
    inventoryAppliedDefault: true,
    inventoryAppliedEditable: false,
    allowedRoles: ["admin", "manager", "chemist"],
    supplierMode: "none",
    purchaserMode: "none",
    priceMode: "none",
    counterpartyKind: null,
    requiresFinalCountedAmount: true,
  },
  BATCH_SPLIT: {
    label: TRANSACTION_TYPE_LABELS.BATCH_SPLIT,
    stockEffect: TRANSACTION_STOCK_EFFECTS.computed,
    inventoryAppliedDefault: true,
    inventoryAppliedEditable: false,
    allowedRoles: ["admin", "manager"],
    supplierMode: "none",
    purchaserMode: "none",
    priceMode: "none",
    counterpartyKind: null,
    requiresFinalCountedAmount: false,
  },
} as const satisfies Record<TransactionType, TransactionTypeMetadata>;

export function isTransactionType(type: string): type is TransactionType {
  return transactionTypeSchema.safeParse(type).success;
}

export function getTransactionTypeMetadata(type: TransactionType): TransactionTypeMetadata {
  return TRANSACTION_TYPE_METADATA[type];
}

export function isInventoryAppliedEditable(type: string): boolean {
  return isTransactionType(type) && TRANSACTION_TYPE_METADATA[type].inventoryAppliedEditable;
}

export function getDefaultInventoryApplied(type: string): boolean {
  return isTransactionType(type) ? TRANSACTION_TYPE_METADATA[type].inventoryAppliedDefault : true;
}

export function shouldForceInventoryApplied(type: string): boolean {
  return isTransactionType(type) && !TRANSACTION_TYPE_METADATA[type].inventoryAppliedEditable;
}

export function getTransactionCounterpartyKind(type: string): TransactionCounterpartyKind | null {
  return isTransactionType(type) ? TRANSACTION_TYPE_METADATA[type].counterpartyKind : null;
}

export function getTransactionLabel(type: string): string {
  return isTransactionType(type) ? TRANSACTION_TYPE_METADATA[type].label : type;
}

export function isPurchaseTransaction(type: string): type is "PURCHASE" {
  return type === "PURCHASE";
}

export function isSaleTransaction(type: string): type is "SALE" {
  return type === "SALE";
}

export function isCommercialTransaction(type: string): boolean {
  return isPurchaseTransaction(type) || isSaleTransaction(type);
}

export function canTransactionTypeUseSupplier(type: string): boolean {
  return isTransactionType(type) && TRANSACTION_TYPE_METADATA[type].supplierMode !== "none";
}

export function canTransactionTypeUsePurchaser(type: string): boolean {
  return isTransactionType(type) && TRANSACTION_TYPE_METADATA[type].purchaserMode !== "none";
}

export function canTransactionTypeUsePrice(type: string): boolean {
  return isTransactionType(type) && TRANSACTION_TYPE_METADATA[type].priceMode !== "none";
}

export function requiresFinalCountedAmount(type: string): boolean {
  return isTransactionType(type) && TRANSACTION_TYPE_METADATA[type].requiresFinalCountedAmount;
}


export const TRANSACTION_VALIDATION_MESSAGES = TRANSACTION_MESSAGES;
